import { useEffect, useMemo, useState } from 'react';
import {
  generateDailyReport,
  generateMonthlyReport,
  getReportAuditTrail,
  listMetrics,
  listReportRuns,
  listReportSnapshots,
  type ReportAuditEvent,
} from '../../api/reporting';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import {
  Button,
  ClickableRow,
  Drawer,
  DrawerField,
  EmptyState,
  ErrorNote,
  FieldLabel,
  GroupLabel,
  HintNote,
  ListToolbar,
  LoadingNote,
  PageHeader,
  Select,
  StatusBadge,
  TextInput,
  formatDateTime,
  tableStyle,
  tdStyle,
  thStyle,
  usePagination,
  Pagination,
} from '../../components/common';
import type { MetricDefinition, ReportRun, ReportSnapshot, ReportType } from '../../types/domain';
import { buildCanonicalReportPayload, buildReportCsv, buildReportPdf, computeReportChecksum, downloadBytes, downloadText } from '../../lib/reportExport';

const CONSOLIDATED = '__ALL__';

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}
function monthStartISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

/**
 * Report Center (FASE 12, migration 0050). Daily/monthly report generation
 * over the real report_runs/report_snapshots tables; every export is tied
 * deterministically to one specific report_run_id (the filename embeds it),
 * never a live re-query — a downloaded file always reflects exactly the
 * immutable snapshot it was generated from.
 */
export default function ReportCenterPage() {
  const { hasPermission, context } = usePermissions();
  const { branches } = useBranch();
  const isTenantWide = context?.authenticated === true && context.roles.some((r) => r.branch_id === null);

  const [runs, setRuns] = useState<ReportRun[]>([]);
  const [metrics, setMetrics] = useState<MetricDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<'ALL' | ReportType>('ALL');
  const [selected, setSelected] = useState<ReportRun | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [r, m] = await Promise.all([listReportRuns(), listMetrics()]);
      setRuns(r);
      setMetrics(m);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const branchName = (id: string | null) => (id ? branches.find((b) => b.branch_id === id)?.name ?? '—' : 'Semua Cabang (Konsolidasi)');

  const filtered = useMemo(() => runs.filter((r) => typeFilter === 'ALL' || r.report_type === typeFilter), [runs, typeFilter]);
  const { pageRows, page, pageCount, setPage, start, pageSize, total } = usePagination(filtered, 15);

  return (
    <div>
      <PageHeader
        title="Report Center"
        subtitle="Generasi laporan harian/bulanan nyata (report_runs/report_snapshots) — setiap snapshot immutable dan tetap dapat direproduksi meski data sumber berubah setelahnya."
      />

      {hasPermission('report', 'run') && (
        <GenerateForm branches={branches} isTenantWide={isTenantWide} onGenerated={() => void load()} />
      )}
      {!hasPermission('report', 'run') && <HintNote text="Peran Anda hanya dapat melihat laporan (report.read), tidak dapat men-generate laporan baru." />}

      {error && <ErrorNote text={error} />}
      {loading && <LoadingNote />}

      {!loading && (
        <>
          <ListToolbar
            resultCount={total}
            filters={
              <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as 'ALL' | ReportType)} style={{ width: 180 }}>
                <option value="ALL">Semua jenis</option>
                <option value="DAILY">Harian</option>
                <option value="MONTHLY">Bulanan</option>
              </Select>
            }
          />
          {runs.length === 0 && <EmptyState text="Belum ada laporan yang di-generate." />}
          {runs.length > 0 && total === 0 && <EmptyState text="Tidak ada laporan yang cocok dengan filter." />}
          {total > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Jenis</th>
                  <th style={thStyle}>Periode</th>
                  <th style={thStyle}>Cabang</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Diminta</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((r) => (
                  <ClickableRow key={r.report_run_id} onClick={() => setSelected(r)}>
                    <td style={tdStyle}>{r.report_type === 'DAILY' ? 'Harian' : 'Bulanan'}</td>
                    <td style={tdStyle}>{r.period_start === r.period_end ? r.period_start : `${r.period_start} — ${r.period_end}`}</td>
                    <td style={tdStyle}>{branchName(r.branch_id)}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={r.status} />
                    </td>
                    <td style={tdStyle}>{formatDateTime(r.requested_at)}</td>
                  </ClickableRow>
                ))}
              </tbody>
            </table>
          )}
          <Pagination page={page} pageCount={pageCount} setPage={setPage} start={start} pageSize={pageSize} total={total} />
        </>
      )}

      <ReportDetailDrawer report={selected} open={!!selected} onClose={() => setSelected(null)} metrics={metrics} branchName={branchName} />
    </div>
  );
}

function GenerateForm({
  branches,
  isTenantWide,
  onGenerated,
}: {
  branches: { branch_id: string; name: string }[];
  isTenantWide: boolean;
  onGenerated: () => void;
}) {
  const { activeBranchId } = useBranch();
  const [reportType, setReportType] = useState<ReportType>('DAILY');
  const [scope, setScope] = useState(activeBranchId ?? CONSOLIDATED);
  const [date, setDate] = useState(todayISO());
  const [monthStart, setMonthStart] = useState(monthStartISO());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const branchId = scope === CONSOLIDATED ? null : scope;
      // Fresh key per submit: this form submission is always a new
      // intentional generate action. Reusing a key across submits would
      // silently return the FIRST submission's report_run every time.
      const idempotencyKey = crypto.randomUUID();
      const runId =
        reportType === 'DAILY'
          ? await generateDailyReport(branchId, date, idempotencyKey)
          : await generateMonthlyReport(branchId, monthStart, idempotencyKey);
      setSuccess(`Laporan berhasil di-generate (report_run: ${runId.slice(0, 8)}...).`);
      onGenerated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="alex-card" style={{ padding: 16, marginBottom: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ width: 150 }}>
        <FieldLabel label="Jenis Laporan">
          <Select value={reportType} onChange={(e) => setReportType(e.target.value as ReportType)}>
            <option value="DAILY">Harian</option>
            <option value="MONTHLY">Bulanan</option>
          </Select>
        </FieldLabel>
      </div>
      <div style={{ width: 220 }}>
        <FieldLabel label="Cakupan">
          <Select value={scope} onChange={(e) => setScope(e.target.value)}>
            {isTenantWide && <option value={CONSOLIDATED}>Semua Cabang (Konsolidasi)</option>}
            {branches.map((b) => (
              <option key={b.branch_id} value={b.branch_id}>
                {b.name}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </div>
      {reportType === 'DAILY' ? (
        <div style={{ width: 170 }}>
          <FieldLabel label="Tanggal">
            <TextInput type="date" value={date} onChange={(e) => setDate(e.target.value)} max={todayISO()} />
          </FieldLabel>
        </div>
      ) : (
        <div style={{ width: 170 }}>
          <FieldLabel label="Bulan (awal bulan)">
            <TextInput type="date" value={monthStart} onChange={(e) => setMonthStart(e.target.value)} />
          </FieldLabel>
        </div>
      )}
      <Button variant="primary" loading={busy} onClick={() => void submit()}>
        Generate Laporan
      </Button>
      {success && <span style={{ color: 'var(--alex-success)', fontSize: 12, fontWeight: 600 }}>{success}</span>}
      {error && <ErrorNote text={error} />}
    </div>
  );
}

function ReportDetailDrawer({
  report,
  open,
  onClose,
  metrics,
  branchName,
}: {
  report: ReportRun | null;
  open: boolean;
  onClose: () => void;
  metrics: MetricDefinition[];
  branchName: (id: string | null) => string;
}) {
  const [snapshots, setSnapshots] = useState<ReportSnapshot[]>([]);
  const [auditTrail, setAuditTrail] = useState<ReportAuditEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exportBusy, setExportBusy] = useState<'csv' | 'pdf' | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [lastChecksum, setLastChecksum] = useState<{ kind: string; value: string } | null>(null);

  const metricByCode = useMemo(() => {
    const map = new Map<string, MetricDefinition>();
    for (const m of metrics) map.set(m.metric_code, m);
    return map;
  }, [metrics]);

  useEffect(() => {
    if (!report) return;
    setLoading(true);
    setError(null);
    setExportError(null);
    setLastChecksum(null);
    Promise.all([listReportSnapshots(report.report_run_id), getReportAuditTrail(report.report_run_id)])
      .then(([snaps, trail]) => {
        setSnapshots(snaps);
        setAuditTrail(trail);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [report?.report_run_id]);

  if (!report) return null;

  /**
   * Both exports are built from the SAME canonical payload derived from the
   * real, already-fetched report_run/report_snapshots rows — never from a
   * live re-query or browser DOM state. A failure here (e.g. pdf-lib
   * throwing) is surfaced honestly — no "download ready" is ever shown, and
   * the underlying report_run/report_snapshots rows are untouched either
   * way (export is read-only).
   */
  const exportCsv = async () => {
    setExportBusy('csv');
    setExportError(null);
    try {
      const payload = buildCanonicalReportPayload(report, snapshots, metricByCode);
      const checksum = await computeReportChecksum(payload);
      const csv = buildReportCsv(payload);
      downloadText(`report_${report.report_run_id}.csv`, csv, 'text/csv;charset=utf-8');
      setLastChecksum({ kind: 'CSV', value: checksum });
    } catch (e) {
      setExportError(`Ekspor CSV gagal: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setExportBusy(null);
    }
  };

  const exportPdf = async () => {
    setExportBusy('pdf');
    setExportError(null);
    try {
      const payload = buildCanonicalReportPayload(report, snapshots, metricByCode);
      const checksum = await computeReportChecksum(payload);
      const bytes = await buildReportPdf(payload, branchName(report.branch_id), new Date());
      downloadBytes(`report_${report.report_run_id}.pdf`, bytes, 'application/pdf');
      setLastChecksum({ kind: 'PDF', value: checksum });
    } catch (e) {
      setExportError(`Ekspor PDF gagal: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setExportBusy(null);
    }
  };

  return (
    <Drawer open={open} onClose={onClose} title={`Laporan ${report.report_type === 'DAILY' ? 'Harian' : 'Bulanan'} — ${report.period_start}`}>
      <DrawerField label="Cabang" value={branchName(report.branch_id)} />
      <DrawerField label="Periode" value={report.period_start === report.period_end ? report.period_start : `${report.period_start} — ${report.period_end}`} />
      <DrawerField label="Status" value={<StatusBadge status={report.status} />} />
      {report.freshness_note && <DrawerField label="Catatan Kesegaran" value={report.freshness_note} />}
      {report.status === 'FAILED' && report.failure_reason && <ErrorNote text={`Alasan gagal: ${report.failure_reason}`} />}
      <DrawerField label="report_run_id" value={<code style={{ fontSize: 11 }}>{report.report_run_id}</code>} />

      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}

      {!loading && report.status !== 'FAILED' && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
            <GroupLabel>Metrik ({snapshots.length})</GroupLabel>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="secondary" loading={exportBusy === 'csv'} onClick={() => void exportCsv()} disabled={snapshots.length === 0 || exportBusy !== null} style={{ padding: '0 10px' }}>
                Ekspor CSV
              </Button>
              <Button variant="secondary" loading={exportBusy === 'pdf'} onClick={() => void exportPdf()} disabled={snapshots.length === 0 || exportBusy !== null} style={{ padding: '0 10px' }}>
                Ekspor PDF
              </Button>
            </div>
          </div>
          {exportError && <ErrorNote text={exportError} />}
          {lastChecksum && !exportError && (
            <p style={{ fontSize: 10.5, color: 'var(--alex-text-faint)', marginTop: -2, marginBottom: 8, fontFamily: 'monospace', wordBreak: 'break-all' }}>
              Checksum data {lastChecksum.kind} (SHA-256, dari report_run_id + metrik): {lastChecksum.value}
            </p>
          )}
          {snapshots.length === 0 && <EmptyState text="Tidak ada metrik terlihat (bisa jadi seluruhnya bersifat sensitif dan tidak berwenang ditampilkan)." variant="section" />}
          {snapshots.length > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Metrik</th>
                  <th style={thStyle}>Nilai</th>
                </tr>
              </thead>
              <tbody>
                {snapshots.map((s) => (
                  <tr key={s.snapshot_id}>
                    <td style={tdStyle}>{metricByCode.get(s.metric_code)?.name ?? s.metric_code}</td>
                    <td style={tdStyle}>{s.computed_value.toLocaleString('id-ID')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {!loading && auditTrail.length > 0 && (
        <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--alex-border-soft)' }}>
          <GroupLabel>Jejak Audit</GroupLabel>
          {auditTrail.map((ev) => (
            <div key={ev.event_id} style={{ padding: '6px 0', borderBottom: '1px solid var(--alex-border-soft)', fontSize: 11.5 }}>
              <b>{ev.action}</b> · {formatDateTime(ev.occurred_at)}
            </div>
          ))}
        </div>
      )}
    </Drawer>
  );
}
